import { FC, ReactNode } from 'react';

import classNames from 'classnames';

import dataAttributes from '../../../../utils/attributes/attributes';
import { Icon } from '../../../icon/components/Icon/Icon';
import { Spinner } from '../../../spinner/components/Spinner/Spinner';
import { Typography } from '../../../typography/components/Typography/Typography';

interface Props {
   label?: ReactNode;
   leadingIcon?: string;
   loading?: boolean;
   toggled?: boolean;
   disabled?: boolean;
   attributes?: Record<string, string>;
   onClick?: () => void;
}

export const ToggleChip: FC<Props> = ({ label, leadingIcon, loading = false, toggled = false, disabled = false, attributes, onClick }) => {
   const hasLeadingElement = loading || !!leadingIcon;

   return (
      <button
         type="button"
         {...dataAttributes(attributes)}
         onClick={(e) => {
            e.currentTarget.blur();
            onClick?.();
         }}
         disabled={disabled}
         className={classNames(
            'rounded-component-data-display-toggle-chip-container-border-radius',
            'border-component-data-display-toggle-chip-container-border-width-default',
            'border-component-data-display-toggle-chip-container-color-border-enabled',
            'py-component-data-display-toggle-chip-container-spacing-padding-y',
            'min-h-component-data-display-toggle-chip-container-sizing-min-height',
            'gap-component-data-display-toggle-chip-container-spacing-gap',
            'flex items-center',
            'px-component-data-display-toggle-chip-container-spacing-padding-x-default',
            {
               'pl-component-data-display-toggle-chip-container-spacing-padding-x-no-icon': !hasLeadingElement,
               'pl-component-data-display-toggle-chip-container-spacing-padding-x-icon': hasLeadingElement,
               'bg-component-data-display-toggle-chip-container-color-background-disabled': disabled,
               'bg-component-data-display-toggle-chip-container-color-background-enabled': !disabled,
               'bg-component-data-display-toggle-chip-container-color-background-toggled': toggled,
               'enabled:hover:bg-component-data-display-toggle-chip-container-color-background-hover-default focus:bg-component-data-display-toggle-chip-container-color-background-focused-default focus:border-component-data-display-toggle-chip-container-color-border-focused active:bg-component-data-display-toggle-chip-container-color-background-pressed-default':
                  !toggled,
               'enabled:hover:bg-component-data-display-toggle-chip-container-color-background-hover-toggled focus:bg-component-data-display-toggle-chip-container-color-background-focused-toggled focus:border-component-data-display-toggle-chip-container-color-border-focused active:bg-component-data-display-toggle-chip-container-color-background-pressed-toggled':
                  toggled
            }
         )}
      >
         {hasLeadingElement && (
            <div
               data-testid="toggle-chip--leading-icon"
               className={classNames({
                  'text-component-data-display-toggle-chip-icon-color-fill-on-disabled-default': disabled && !toggled,
                  'text-component-data-display-toggle-chip-icon-color-fill-on-disabled-toggled': disabled && toggled,
                  'text-component-data-display-toggle-chip-icon-color-fill-primary-default': !disabled && !toggled,
                  'text-component-data-display-toggle-chip-icon-color-fill-primary-toggled': !disabled && toggled
               })}
            >
               {loading ? <Spinner size="small" /> : <Icon icon={leadingIcon!} />}
            </div>
         )}
         {label && (
            <span
               data-testid="toggle-chip--label"
               className={classNames({
                  'text-component-data-display-toggle-chip-label-color-text-on-disabled-default': disabled && !toggled,
                  'text-component-data-display-toggle-chip-label-color-text-on-disabled-toggled': disabled && toggled,
                  'text-component-data-display-toggle-chip-label-color-text-on-theme-default': !disabled && !toggled,
                  'text-component-data-display-toggle-chip-label-color-text-on-theme-toggled': !disabled && toggled
               })}
            >
               <Typography color="inherit" variant="small">
                  {label}
               </Typography>
            </span>
         )}
      </button>
   );
};
